import { LoadingSpinner } from '@/components/LoadingSpinner.tsx'
import { ShareDialog } from '@/components/ShareDialog.tsx'
import { ProjectCardChart } from '@/components/analytics/ProjectCardChart'
import { Button } from '@/components/ui/button.tsx'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card.tsx'
import { Separator } from '@/components/ui/separator'
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

interface ProjectCardProps {
  project: ProjectMeta
  iterations: ProjectCardIteration[]
  loading: boolean
}

export const ProjectCard = ({ project, iterations, loading }: ProjectCardProps) => {
  const navigate = useNavigate()

  const latestIteration = useMemo(() => {
    return iterations.length > 0 ? iterations[iterations.length - 1] : null
  }, [iterations])

  return (
    <Card
      className="min-w-[340px] flex-1 bg-white border-none shadow-none py-3 px-4 cursor-pointer rounded-sm"
      onClick={() => navigate(`/project/${project.id}`)}
    >
      <CardHeader className="p-0">
        <div className="flex items-center justify-between">
          <CardTitle tabIndex={0} className="text-base font-bold text-contents-primary truncate">
            {project.name}
          </CardTitle>
          <ShareButton link={project.invitationLink} />
        </div>
      </CardHeader>
      <Separator className="my-2" />
      <CardContent className="py-4 p-0 pt-3 relative">
        {loading ? (
          <LoadingView />
        ) : iterations.length === 0 ? (
          <EmptyStateView />
        ) : (
          <DataContentView latestIteration={latestIteration} iterations={iterations} />
        )}
      </CardContent>
    </Card>
  )
}

const ShareButton = ({ link }: { link: string }) => (
  // biome-ignore lint/a11y/useKeyWithClickEvents: click event should not propagate to parent
  <div onClick={(e) => e.stopPropagation()}>
    <ShareDialog link={link}>
      <Button type="button" size="icon" variant="ghost">
        <img src="/images/share-icon.svg" alt="Share" />
      </Button>
    </ShareDialog>
  </div>
)

const LoadingView = () => (
  <div className="h-full flex items-center justify-center">
    <LoadingSpinner />
  </div>
)

const EmptyStateView = () => (
  <div className="h-full flex items-center justify-center text-contents-tertiary text-xs">No data available</div>
)

const MetricItem = ({ label, value }: { label: string; value: string | number | undefined }) => (
  <div className="flex items-center gap-1">
    <span className="text-EN-KR-body2-regular text-contents-primary">{label}</span>
    <span className="text-EN-KR-body2-bold font-bold text-contents-primary">{value ?? '-'}</span>
  </div>
)

const DataContentView = ({
  latestIteration,
  iterations,
}: {
  latestIteration: ProjectCardIteration | null
  iterations: ProjectCardIteration[]
}) => (
  <div className="flex items-end justify-between gap-10">
    <div className="flex flex-col w-[72px] gap-2">
      <MetricItem label="Velocity" value={latestIteration?.velocity} />
      <MetricItem label="Volatility" value={latestIteration?.volatility} />
    </div>
    <div className="flex flex-1 h-[100px]">
      <ProjectCardChart iterations={iterations} />
    </div>
  </div>
)
